import { styled } from "@mui/material";
import { FormattedMessage } from "react-intl";

import { Icon } from "@/shared/components/common/Icon/Icon";

import { FieldClearButton, FieldError } from "../FieldParts";
import {
  Container,
  IconSlot,
  InputWrapper,
  Label,
  TriggerText,
} from "../shared.styles";
import { useSubmitFirstField } from "../useSubmitFirstField";

import type { FieldBaseProps } from "../types";

type FileFieldBaseProps = FieldBaseProps & {
  value: File | null;
  accept?: string;
  onChange?: (file: File | null) => void;
};

const FileFieldBase = ({
  accept,
  disabled,
  error,
  fullWidth,
  label,
  name,
  onBlur,
  onChange,
  placeholder,
  value,
}: FileFieldBaseProps): React.ReactElement => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    onChange?.(e.target.files?.[0] ?? null);
    e.target.value = "";
  };

  const handleClear = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onChange?.(null);
  };

  return (
    <Container $fullWidth={fullWidth}>
      {label && <Label htmlFor={name}>{label}</Label>}
      <FileInputWrapper $disabled={!!disabled} $error={!!error}>
        <OverlayFileInput
          accept={accept}
          data-testid={name}
          disabled={disabled}
          id={name}
          name={name}
          onBlur={() => onBlur?.()}
          onChange={handleInputChange}
          type="file"
        />
        <FileTriggerText $hasValue={!!value}>
          {value?.name ?? placeholder ?? (
            <FormattedMessage id="FileField.placeholder" />
          )}
        </FileTriggerText>
        {value && !disabled ? (
          <ClearSlot>
            <FieldClearButton name={name} onClick={handleClear} />
          </ClearSlot>
        ) : (
          <IconSlot $disabled={disabled}>
            <Icon name="file" />
          </IconSlot>
        )}
      </FileInputWrapper>
      <FieldError error={error} name={name} />
    </Container>
  );
};

type FileFieldProps = {
  name: string;
  accept?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  label?: React.ReactNode;
  placeholder?: string;
};

const FileField = ({ name, ...props }: FileFieldProps): React.ReactElement => {
  const { error, handleBlur, setValue, value } =
    useSubmitFirstField<File | null>(name);

  return (
    <FileFieldBase
      {...props}
      error={error}
      name={name}
      onBlur={handleBlur}
      onChange={setValue}
      value={value}
    />
  );
};

const FileInputWrapper = styled(InputWrapper)({
  position: "relative",
  cursor: "pointer",
});

const FileTriggerText = styled(TriggerText)({
  flex: 1,
});

// The native input covers the whole field invisibly, so a click anywhere opens
// the picker and focus lands on a real file input (keyboard + focus-within glow
// for free). opacity, not display/visibility — a hidden input is not focusable.
const OverlayFileInput = styled("input")({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  opacity: 0,
  cursor: "pointer",
  "&:disabled": {
    cursor: "not-allowed",
  },
});

const ClearSlot = styled("span")({
  position: "relative",
  zIndex: 1,
  display: "flex",
});

export { FileField, FileFieldBase };
